/* Journal scroll parallax — two of the reference's scrub effects, no GSAP.

   1. [data-journal-parallax] — their [img-scroll] (about-page effect; used by
      our article/how-it-works/legal/about images — NOT the journal rows,
      whose photos are static like the reference's homepage): each image is
      oversized (125% height, -25% top) inside its clipped frame and slides
      from 0% to +20% of its own height as the frame travels the viewport.

   2. [data-journal-overlap] — their data-scroll-overlap (homepage community
      rows): once a row's top crosses the viewport top, the whole row drifts
      down over the next 500px of scroll, eased power1.in (p²) — so it lingers
      while the next row rides up over it and its opaque card surface (CSS in
      journal.liquid, ground-coloured like their .community-item's white)
      slices the drifted photo away from the bottom — it never shows through
      the next row's text. The gap the drift opens above the row is always
      offscreen (it only starts past the top). Runs on every viewport.

      Drift AMOUNT:
      - Desktop (≥1200px): 50% of the ROW height — the reference's literal
        yPercent:50 (verified against the live site). Approved as-is.
      - Mobile (<1200px, the journal's stacked breakpoint): 86% of the PHOTO
        height instead. The reference ties it to row height, but their row's
        text is short; ours varies per blog post, and 50%-of-row grows the
        drift whenever the excerpt is longer. Anchoring to the photo (their
        measured live ratio: 161px drift on a 187px photo = 0.86) keeps the
        photo's motion identical to theirs no matter how tall the text is —
        the frame carries a [data-journal-photo] marker so we can measure it.

   Runs on desktop and mobile; skipped for reduced motion.
   Registry-managed: re-queries its elements after every Barba swap; the old
   page's scroll/resize listeners die with the abort signal. */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // mobile = the journal's own stacked-layout breakpoint. Live listener (not
  // a load-time snapshot) so rotate/resize re-evaluate, like about-flick.js
  const mobileMq = window.matchMedia('(max-width: 1199px)');
  const PHOTO_RATIO = 0.86; // reference's measured photo drift (161px / 187px)

  const init = (ctx) => {
    const signal = ctx && ctx.signal;

    const imgs = [...document.querySelectorAll('[data-journal-parallax]')];
    const rows = [...document.querySelectorAll('[data-journal-overlap]')];
    if (!imgs.length && !rows.length) return;

    // applied drift per row (px) — subtracted from the measured rect so the
    // trigger maths always run on the row's natural (untransformed) position,
    // not a position we moved ourselves (no feedback loop)
    const drift = new WeakMap();

    // max drift for a row: mobile → 86% of its photo height (text-length-proof);
    // desktop → 50% of the row height (the reference's literal yPercent:50)
    const maxDrift = (row, rect) => {
      if (mobileMq.matches) {
        const photo = row.querySelector('[data-journal-photo]');
        if (photo) return photo.getBoundingClientRect().height * PHOTO_RATIO;
      }
      return rect.height * 0.5;
    };

    let ticking = false;

    const update = () => {
      ticking = false;
      const vh = window.innerHeight;

      imgs.forEach((img) => {
        const frame = img.parentElement;
        const rect = frame.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return; // off-screen

        // 0 when the frame enters at the bottom, 1 when it leaves at the top
        const progress = (vh - rect.top) / (vh + rect.height);
        const clamped = Math.max(0, Math.min(1, progress));
        img.style.transform = `translateY(${(clamped * 20).toFixed(2)}%)`;
      });

      rows.forEach((row) => {
        const rect = row.getBoundingClientRect();
        const shift = drift.get(row) || 0;
        const naturalTop = rect.top - shift;
        if (naturalTop > 0 && shift === 0) return; // below the line, untouched

        // reference: start "top top", 500px of scroll, power1.in (p²); the max
        // travel differs desktop vs mobile — see maxDrift()
        const p = Math.max(0, Math.min(1, -naturalTop / 500));
        const px = maxDrift(row, rect) * p * p;
        if (Math.abs(px - shift) < 0.1) return;
        row.style.transform = px > 0 ? `translateY(${px.toFixed(1)}px)` : '';
        drift.set(row, px);
      });
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true, signal });
    window.addEventListener('resize', onScroll, { passive: true, signal });
    // crossing the breakpoint switches the drift basis — clear stale transforms
    // so rows below the trigger don't keep a value from the other mode
    mobileMq.addEventListener('change', () => {
      rows.forEach((row) => { row.style.transform = ''; drift.delete(row); });
      onScroll();
    }, { signal });
    update();
  };

  if (window.Onni && window.Onni.register) {
    window.Onni.register('journal-scroll', init);
  } else {
    init();
  }
})();
